import { ReactNode, useRef } from 'react';
import { redirectToMenuAfterSignIn } from './entryRedirect';
import { ClerkProvider, useUser, useAuth, SignIn, ClerkLoaded, ClerkLoading } from '@clerk/clerk-react';
import { esMX } from '@clerk/localizations';
import { LccUser } from './types';
import { setTokenProvider } from './tokenProvider';
import { useTheme, ThemeId } from '../theme/ThemeProvider';

/**
 * Traduccion e imagen del widget de Clerk (<SignIn/> y cualquier otro que se
 * llegue a usar - <SignUp/>, <UserProfile/>, etc). `esMX` es la traduccion
 * oficial de Clerk al espanol (paquete @clerk/localizations) - cubre TODOS
 * los textos del widget, no solo esta pantalla.
 *
 * Clerk dibuja este widget en su propio DOM y no lee las variables CSS de la
 * app, asi que para que combine con los 4 temas visuales (Claro/Sepia/Oscuro/
 * WMS) hay que pasarle un `appearance` DISTINTO segun el tema activo - los
 * valores de cada uno son los mismos hex que usa shared/theme/globals.css
 * para ese tema (colorPrimary = brand-500, colorBackground = surface, etc).
 */
const CLERK_APPEARANCE_BY_THEME: Record<ThemeId, { variables: Record<string, string>; elements: Record<string, string> }> = {
  claro: {
    variables: {
      colorPrimary: '#1fa66b',
      colorText: '#2c322e',
      colorTextSecondary: '#6b736e',
      colorBackground: '#ffffff',
      colorInputBackground: '#ffffff',
      colorInputText: '#2c322e',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '1rem',
    },
    elements: { card: 'shadow-none' },
  },
  sepia: {
    variables: {
      colorPrimary: '#8a6d3b',
      colorText: '#3a3124',
      colorTextSecondary: '#8a795a',
      colorBackground: '#fbf5e6',
      colorInputBackground: '#fbf5e6',
      colorInputText: '#3a3124',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '1rem',
    },
    elements: { card: 'shadow-none' },
  },
  oscuro: {
    variables: {
      colorPrimary: '#34b077',
      colorText: '#d7dce3',
      colorTextSecondary: '#7c8695',
      colorBackground: '#171c22',
      colorInputBackground: '#1b2028',
      colorInputText: '#d7dce3',
      colorNeutral: '#2e3641',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '1rem',
    },
    elements: { card: 'shadow-none' },
  },
  wms: {
    variables: {
      colorPrimary: '#2f80ed',
      colorText: '#dde1e9',
      colorTextSecondary: '#7e899e',
      colorBackground: '#141b28',
      colorInputBackground: '#161d2b',
      colorInputText: '#dde1e9',
      colorNeutral: '#2b3549',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '1rem',
    },
    elements: { card: 'shadow-none' },
  },
};

/**
 * Autenticacion de PRODUCCION con Clerk.
 * Requiere VITE_CLERK_PUBLISHABLE_KEY. El backend (Worker) valida el JWT de Clerk
 * en cada peticion (ver apps/api/src/shared/auth/verifyClerk.ts).
 *
 * El nombre del colaborador se toma automaticamente del usuario autenticado
 * (recomendacion del documento de especificacion): nunca se vuelve a pedir
 * en el formulario de Control de Tiempos.
 */
export function ClerkAuthProvider({ children }: { children: ReactNode }) {
  const key = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;
  const { theme } = useTheme();
  return (
    <ClerkProvider publishableKey={key} localization={esMX} appearance={CLERK_APPEARANCE_BY_THEME[theme]}>
      <ClerkLoading>
        <div className="flex h-screen items-center justify-center text-ink-400">Cargando…</div>
      </ClerkLoading>
      <ClerkLoaded>
        <ClerkGate>{children}</ClerkGate>
      </ClerkLoaded>
    </ClerkProvider>
  );
}

function ClerkGate({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();

  // IMPORTANTE: se registra de forma sincrona durante el render (no en useEffect).
  // Los useEffect de los componentes hijos (ej. la carga inicial de actividades/usuarios
  // en OperationTimesPage) se ejecutan ANTES que los useEffect de este componente padre,
  // asi que si el getToken se registrara en un useEffect aqui, las peticiones iniciales
  // saldrian con el token en null y el backend responderia 401 "No autenticado".
  setTokenProvider(() => getToken());

  // Si en esta carga se vio el login, al entrar se va SIEMPRE al Menu de aplicaciones (no a la
  // pantalla que estaba en la URL). Se hace antes de montar el router (que vive en children).
  const sawSignIn = useRef(false);
  if (!isSignedIn) {
    sawSignIn.current = true;
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-subtle p-6">
        <SignIn forceRedirectUrl="/" signUpForceRedirectUrl="/" />
      </div>
    );
  }
  if (sawSignIn.current) {
    sawSignIn.current = false;
    redirectToMenuAfterSignIn();
  }
  return <>{children}</>;
}

export function useClerkCurrentUser(): LccUser {
  const { user } = useUser();
  return {
    id: user?.id ?? 'unknown',
    fullName: user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'Usuario',
    role: (user?.publicMetadata?.role as LccUser['role']) ?? 'operator',
  };
}
